/**
 * Two-panel requirement browser: left = reqId list, right = selected detail.
 * The detail panel is collapsible and its divider is draggable.
 */

import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, Empty, Modal, Spin, Tag, message } from 'antd';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { getBspTagColor } from '@/utils/tagColor';
import { deleteRequirements } from '@/api/requirements';
import RequirementCard from './RequirementCard';
import {
  DEFAULT_RIGHT,
  DIVIDER_WIDTH,
  MIN_LEFT,
  MIN_RIGHT,
  resolvePanelWidth,
} from './detailPanelLayout';
import {
  resolveRequirementSelection,
  selectionRequirement,
} from './selectedRequirement';
import type { Requirement } from '@/types';
import { useFlowbackStore } from '@/store/flowbackStore';
import { useAuth } from '@/hooks/useAuth';
import LaggingBubble from '../flowback/LaggingBubble';

interface RequirementListProps {
  requirements: Requirement[];
  /** Unfiltered requirements, used to keep the detail panel working when the
   *  selected req is hidden by a filter keyword or the active subcategory tab. */
  knownRequirements?: Requirement[];
  isLoading: boolean;
  error: string | null;
  projectId: number;
  projectName?: string;
  isMainline?: boolean;
  initialSelectedReqId?: string | null;
  canEdit?: boolean;
  canDelete?: boolean;
  notifyList?: Set<string>;
  onToggleNotifyList?: (reqId: string) => void;
  onOpenNotifyModal?: () => void;
  /** Clear the filters / switch tab so the hidden requirement becomes visible. */
  onLocateRequirement?: (requirement: Requirement) => void;
}

const RequirementList: React.FC<RequirementListProps> = ({
  requirements,
  knownRequirements,
  isLoading,
  error,
  projectId,
  projectName,
  isMainline,
  initialSelectedReqId,
  canEdit,
  canDelete,
  notifyList,
  onToggleNotifyList,
  onOpenNotifyModal,
  onLocateRequirement,
}) => {
  const navigate = useNavigate();
  const { isExternal } = useAuth();
  const [selectedReqId, setSelectedReqId] = useState<string | null>(initialSelectedReqId ?? null);
  const [displayedRequirements, setDisplayedRequirements] = useState(requirements);
  // Width the user dragged to. The rendered width is derived from it on every
  // render, so a transient narrow measurement (hidden tab pane → 0) cannot
  // permanently clamp the panel down to MIN_RIGHT.
  const [preferredRightWidth, setPreferredRightWidth] = useState(DEFAULT_RIGHT);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState(!initialSelectedReqId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Mirrors edits made to a requirement that is not in the visible list, so the
  // hidden-detail card reflects saves instead of showing the stale known copy.
  const [selectedOverride, setSelectedOverride] = useState<Requirement | null>(null);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const dragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listPanelRef = useRef<HTMLDivElement>(null);

  // When parent requests a specific reqId (e.g. from overview click), update selection
  useEffect(() => {
    if (initialSelectedReqId) {
      setSelectedReqId(initialSelectedReqId);
      setCollapsed(false);
    }
  }, [initialSelectedReqId]);

  // Sync displayed requirements when props change
  useEffect(() => {
    setDisplayedRequirements(requirements);
  }, [requirements]);

  // A different requirement is selected → drop the previous edit override.
  useEffect(() => {
    setSelectedOverride(null);
  }, [selectedReqId]);

  // Track the container width purely as an input for the width calculation. A
  // hidden antd tab pane reports 0: that measurement says nothing about the
  // available space, so it is dropped rather than read as "no room".
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = (width: number) => {
      if (width > 0) setContainerWidth(width);
    };
    measure(container.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) measure(entry.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Drop selected ids that are no longer visible (filter change, tab switch, deletion)
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(displayedRequirements.map((r) => r.reqId));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [displayedRequirements]);

  // After requirements load, scroll selected item into view
  useEffect(() => {
    if (!selectedReqId || !listPanelRef.current) return;
    const el = listPanelRef.current.querySelector<HTMLElement>(`[data-req-id="${CSS.escape(selectedReqId)}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedReqId, displayedRequirements]);

  // Fetch requirements lagging behind mainline (yellow bubble) + flowback eligibility
  const { laggingSet, fetchLagging, fetchEligibility } = useFlowbackStore();
  useEffect(() => {
    if (projectId) {
      fetchLagging(projectId);
      fetchEligibility(projectId);
    }
  }, [projectId, fetchLagging, fetchEligibility]);

  // Handle requirement update (e.g. bspTag, status changed inline)
  const handleUpdated = (updated: Requirement) => {
    setDisplayedRequirements(prev =>
      prev.map(r => r.reqId === updated.reqId ? { ...r, ...updated } : r)
    );
    // The visible list may not contain a hidden selection; keep its card fresh.
    setSelectedOverride((prev) => (updated.reqId === selectedReqId ? updated : prev));
  };

  // Handle requirement deletion
  const handleDeleted = (reqId: string) => {
    const filtered = displayedRequirements.filter(r => r.reqId !== reqId);
    setDisplayedRequirements(filtered);
    if (selectedReqId === reqId) {
      setSelectedReqId(filtered.length > 0 ? filtered[0].reqId : null);
    }
  };

  const toggleSelect = (reqId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(reqId)) {
        next.delete(reqId);
      } else {
        next.add(reqId);
      }
      return next;
    });
  };

  const handleBatchDelete = () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;

    Modal.confirm({
      title: `确认删除选中的 ${ids.length} 条需求？`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <p>将删除需求：<strong>{ids.join('、')}</strong></p>
          <p style={{ color: '#ff4d4f', marginTop: 8 }}>
            此操作将同时删除这些需求的所有评论和附件，且不可恢复。
          </p>
        </>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setBatchDeleting(true);
        try {
          const result = await deleteRequirements(projectId, ids);
          const deletedSet = new Set(result.deletedIds);
          const remaining = displayedRequirements.filter((r) => !deletedSet.has(r.reqId));
          setDisplayedRequirements(remaining);
          setSelectedIds(new Set(result.failed.map((f) => f.requirementId)));
          if (selectedReqId && deletedSet.has(selectedReqId)) {
            setSelectedReqId(remaining.length > 0 ? remaining[0].reqId : null);
          }
          if (result.deletedIds.length > 0) {
            message.success(`已删除 ${result.deletedIds.length} 条需求`);
          }
          if (result.failed.length > 0) {
            Modal.warning({
              title: `${result.failed.length} 条需求删除失败`,
              content: (
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {result.failed.map((f) => (
                    <div key={f.requirementId}>{f.requirementId}：{f.message}</div>
                  ))}
                </div>
              ),
            });
          }
          if (result.deletedFromProjects.length > 0) {
            Modal.info({
              title: '主线同步提示',
              content: `主线项目 ${result.deletedFromProjects.join('、')} 包含被删除需求，已自动删除`,
            });
          }
        } finally {
          setBatchDeleting(false);
        }
      },
    });
  };

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Store the raw drag position; clamping happens at render time so it can
      // be re-evaluated when the container size changes.
      setPreferredRightWidth(rect.right - ev.clientX);
    };

    const onMouseUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // Rendered width is re-derived from the user's preference on every render, so
  // a transient narrow measurement cannot ratchet the panel down permanently.
  const rightWidth = resolvePanelWidth(preferredRightWidth, containerWidth);

  // Visible list = displayedRequirements plus the current prop as a one-cycle-lag
  // fallback (keeps the old `||` chain's precedence: displayed first).
  const visibleRequirements = useMemo(() => {
    if (requirements === displayedRequirements) return displayedRequirements;
    const seen = new Set(displayedRequirements.map((r) => r.reqId));
    const pending = requirements.filter((r) => !seen.has(r.reqId));
    return pending.length > 0 ? [...displayedRequirements, ...pending] : displayedRequirements;
  }, [displayedRequirements, requirements]);

  const selection = resolveRequirementSelection(selectedReqId, visibleRequirements, knownRequirements);
  const selectedBase = selectionRequirement(selection);
  const selected =
    selectedBase && selectedOverride?.reqId === selectedBase.reqId ? selectedOverride : selectedBase;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        title="数据加载失败"
        description={error}
        className="mb-4"
      />
    );
  }

  // Nothing filtered in and nothing selected → the whole page is empty. When a
  // URL reqId is selected, keep rendering so its detail stays reachable.
  if (requirements.length === 0 && selection.kind === 'none') {
    return <Empty description="暂无需求数据" />;
  }

  const selectedCount = selectedIds.size;
  const allSelected =
    displayedRequirements.length > 0 &&
    displayedRequirements.every((r) => selectedIds.has(r.reqId));
  const someSelected = selectedCount > 0 && !allSelected;
  // 勾选原本只为批量删除服务；通知列表批量加入也需要勾选，因此非删除用户也要显示选择性。
  const canSelect = !!canDelete;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)' }}>
      {/* Toolbar: bulk selection + detail panel toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {canSelect && (
            <>
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={() => {
                  setSelectedIds(
                    allSelected
                      ? new Set()
                      : new Set(displayedRequirements.map((r) => r.reqId))
                  );
                }}
              >
                全选
              </Checkbox>
              {selectedCount > 0 && (
                <span style={{ fontSize: 12, color: '#666' }}>已选 {selectedCount} 项</span>
              )}
              {canDelete && (
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={selectedCount === 0}
                  loading={batchDeleting}
                  onClick={handleBatchDelete}
                >
                  批量删除
                </Button>
              )}
            </>
          )}
        </div>
        <Button
          type="text"
          size="small"
          icon={collapsed ? <LeftOutlined /> : <RightOutlined />}
          onClick={() => setCollapsed((prev) => !prev)}
        >
          {collapsed ? '展开详情' : '收起详情'}
        </Button>
      </div>

      <div ref={containerRef} style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left panel: requirement ID list */}
        <div
          ref={listPanelRef}
          style={{
            flex: 1,
            minWidth: MIN_LEFT,
            overflowY: 'auto',
            paddingRight: 8,
          }}
        >
          {displayedRequirements.map((req) => (
            <div
              key={req.reqId}
              data-req-id={req.reqId}
              onClick={() => {
                setSelectedReqId(req.reqId);
                setCollapsed(false);
                navigate(`?reqId=${encodeURIComponent(req.reqId)}`);
              }}
              style={{
                display: 'flex',
                gap: 8,
                padding: '8px 12px',
                marginBottom: 4,
                borderRadius: 6,
                cursor: 'pointer',
                background: req.reqId === selectedReqId ? '#e6f4ff' : 'transparent',
                borderLeft: req.reqId === selectedReqId ? '3px solid #1677ff' : '3px solid transparent',
                transition: 'background 0.2s',
              }}
            >
              {canSelect && (
                <span
                  onClick={(e) => e.stopPropagation()}
                  style={{ paddingTop: 2, flexShrink: 0 }}
                >
                  <Checkbox
                    checked={selectedIds.has(req.reqId)}
                    onChange={() => toggleSelect(req.reqId)}
                  />
                </span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{req.reqId}</span>
                  {!isMainline && !isExternal && (
                    <LaggingBubble
                      projectId={projectId}
                      projectName={projectName ?? ''}
                      requirementId={req.reqId}
                      subcategory={req.subcategory}
                      lagging={laggingSet.has(req.reqId)}
                    />
                  )}
                  {req.status && (
                    <Tag
                      style={{ fontSize: 11, margin: 0 }}
                      color={
                        req.status === 'delivered' ? 'blue' :
                        req.status === 'released' ? 'green' :
                        req.status === 'updated' ? 'gold' :
                        req.status === 'reject' ? 'red' :
                        'default'
                      }
                    >
                      {req.status}
                    </Tag>
                  )}
                  {req.bspTag && (
                    <Tag style={{ fontSize: 11, margin: 0 }} color={getBspTagColor(req.bspTag!)}>
                      {req.bspTag}
                    </Tag>
                  )}
                  {req.bspTag1 && (
                    <Tag style={{ fontSize: 11, margin: 0 }} color={getBspTagColor(req.bspTag1!)}>
                      {req.bspTag1}
                    </Tag>
                  )}
                  {req.bspTag2 && (
                    <Tag style={{ fontSize: 11, margin: 0 }} color={getBspTagColor(req.bspTag2!)}>
                      {req.bspTag2}
                    </Tag>
                  )}
                </div>
                {req.description && (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#888',
                      marginTop: 4,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {req.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {!collapsed && (
          <>
            {/* Draggable divider (controls detail panel width) */}
            <div
              onMouseDown={onMouseDown}
              style={{
                width: 6,
                cursor: 'col-resize',
                background: '#f0f0f0',
                borderRadius: 3,
                flexShrink: 0,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#d9d9d9'; }}
              onMouseLeave={(e) => { if (!dragging.current) (e.currentTarget as HTMLDivElement).style.background = '#f0f0f0'; }}
            />

            {/* Right panel: selected requirement detail */}
            <div
              style={{
                width: rightWidth,
                // CSS fallback for the first paint, before the resize observer clamps state
                maxWidth: `calc(100% - ${MIN_LEFT + DIVIDER_WIDTH}px)`,
                flexShrink: 0,
                minWidth: MIN_RIGHT,
                overflowY: 'auto',
                paddingLeft: 16,
              }}
            >
              {selection.kind === 'hidden' && selected && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  title={`${selected.reqId} 不在当前列表`}
                  description={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span>该需求可能被过滤条件或分类页签隐藏，详情仍可正常查看。</span>
                      {onLocateRequirement && (
                        <Button size="small" onClick={() => onLocateRequirement(selected)}>
                          定位到该需求
                        </Button>
                      )}
                    </div>
                  }
                />
              )}
              {selected ? (
                <RequirementCard
                  key={selected.reqId}
                  requirement={selected}
                  projectId={projectId}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onUpdated={handleUpdated}
                  onDeleted={handleDeleted}
                  notifyList={notifyList}
                  onToggleNotifyList={onToggleNotifyList}
                  onOpenNotifyModal={onOpenNotifyModal}
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300,
                    color: '#999',
                    textAlign: 'center',
                  }}
                >
                  {selection.kind === 'missing'
                    ? `需求 ${selection.reqId} 未找到，可能已被删除或不在当前项目中`
                    : '请从左侧选择一个需求查看详情'}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RequirementList;
